import { useRef, useState } from "react";
import { createDispatchRemote } from "../lib/dispatchCreate";
import { enqueue, toQueuedDispatch } from "../lib/outbox";
import { isNetworkError, useOnline } from "../lib/net";
import { ensureSession } from "../lib/authState";
import { upsertCrew } from "../lib/crew";

export function useCreateDispatch({ user, profile, refreshWallet }) {
  const idemKey = useRef(crypto.randomUUID());
  const jobToken = useRef(crypto.randomUUID());
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);
  const [errorCode, setErrorCode] = useState("");
  const online = useOnline();

  const queue = async ({ card, contact, pin, siteLabel, knownSite, audioBlob, photos, text, edited }) => {
    if (contact && (contact.phone || contact.email) && !contact.crewContactId && contact.saveToCrew !== false && contact.contactName?.trim()) {
      const row = await upsertCrew(user.id, { name: contact.contactName, phone: contact.phone || null, email: contact.email || null }).catch(() => null);
      if (row) contact = { ...contact, crewContactId: row.id };
    }
    const item = await enqueue({ idempotency_key: idemKey.current, job_token: jobToken.current, card, edited, text, audioBlob, photos: (photos || []).map((p) => p.blob), pin, siteLabel, knownSiteId: knownSite?.id || null, contact, senderName: profile?.full_name || null, markSent: false });
    return toQueuedDispatch(item);
  };

  const create = async (input) => {
    if (inFlight.current) return null;
    inFlight.current = true; setBusy(true); setErrorCode("");
    try {
      if (!navigator.onLine || !online) return await queue(input);
      const auth = await ensureSession();
      if (!auth.session) return await queue(input);
      try {
        const d = await createDispatchRemote({ user, senderName: profile?.full_name, ...input, idemKey: idemKey.current, jobToken: jobToken.current });
        refreshWallet?.(); setTimeout(() => refreshWallet?.(), 1500);
        return d;
      } catch (e) {
        if (e.code === "AUTH" || isNetworkError(e)) return await queue(input);
        if (e.code) { setErrorCode(e.code); throw e; }
        setErrorCode("ERROR"); throw Object.assign(e, { code: "ERROR" });
      }
    } finally { inFlight.current = false; setBusy(false); }
  };

  return { create, busy, errorCode, setErrorCode, idemKey: idemKey.current, jobToken: jobToken.current };
}
